import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MessageCircle } from "lucide-react";
import useSiteConfig, { instagramUrl, whatsappLinkFromCfg } from "@/lib/useSiteConfig";

export default function Footer() {
  const cfg = useSiteConfig();
  const waLink = whatsappLinkFromCfg(cfg);
  const igLink = instagramUrl(cfg.instagram_handle);
  const phoneTel = `+${(cfg.whatsapp_number || "919355012999").replace(/[^\d]/g, "")}`;

  return (
    <footer data-testid="site-footer" className="mt-20 border-t border-forest/15 bg-forest text-parchment">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-4 lg:px-10 lg:py-20">
        <div>
          <Link to="/" className="inline-block">
            <img src="/vyagram-logo.png" alt="Vyagram Experiences" className="h-11 w-auto" />
          </Link>
          <p className="mt-5 font-editorial text-[1.05rem] leading-relaxed text-parchment/95">
            A portfolio of experience-led stays in South Delhi &amp; Gurugram.
            Crafted for considered, premium, versatile hospitality.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={igLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-parchment/30 bg-white/5 px-4 py-2 text-sm hover:bg-white/10" data-testid="footer-instagram">
              <Instagram size={15} /> Instagram
            </a>
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white" data-testid="footer-whatsapp">
              <MessageCircle size={15} /> WhatsApp
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display text-xl text-parchment">Stays</h4>
          <div className="mt-4 flex flex-col gap-2 text-sm text-parchment/90">
            <Link to="/stays" className="hover:text-gold">All Stays</Link>
            <Link to="/collections/couples" className="hover:text-gold">Couples Escapes</Link>
            <Link to="/collections/cinematic" className="hover:text-gold">Cinematic Stays</Link>
            <Link to="/collections/experiential-design" className="hover:text-gold">Experiential Getaways</Link>
            <Link to="/collections/private-gatherings" className="hover:text-gold">Private Gatherings</Link>
            <Link to="/collections/long-stays" className="hover:text-gold">Long Stays &amp; Workations</Link>
            <Link to="/shoots" className="hover:text-gold">For Creators &amp; Commercial Shoots</Link>
          </div>
        </div>

        <div>
          <h4 className="font-display text-xl text-parchment">Locations</h4>
          <div className="mt-4 flex flex-col gap-2 text-sm text-parchment/90">
            <Link to="/locations/south-delhi" className="hover:text-gold">Near Dhan Mill · South Delhi</Link>
            <Link to="/locations/gk1" className="hover:text-gold">Greater Kailash · South Delhi</Link>
            <Link to="/locations/gurugram" className="hover:text-gold">Uppal Southend · Gurugram</Link>
          </div>

          <h4 className="mt-8 font-display text-xl text-parchment">Explore</h4>
          <div className="mt-4 flex flex-col gap-2 text-sm text-parchment/90">
            <Link to="/guest-love" className="hover:text-gold">Guest Love</Link>
            <Link to="/faq" className="hover:text-gold">FAQ</Link>
          </div>
        </div>

        <div>
          <h4 className="font-display text-xl text-parchment">Stay in touch</h4>
          <div className="mt-4 flex flex-col gap-3 text-sm text-parchment/90">
            <a href={`mailto:${cfg.support_email || "stay@vyagram.com"}`} className="inline-flex items-center gap-2 hover:text-gold" data-testid="footer-email"><Mail size={15}/> {cfg.support_email || "stay@vyagram.com"}</a>
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-gold" data-testid="footer-whatsapp-link"><MessageCircle size={15}/> {cfg.whatsapp_display}</a>
            <a href={`tel:${phoneTel}`} className="inline-flex items-center gap-2 hover:text-gold" data-testid="footer-phone"><Phone size={15}/> Call · tap to dial</a>
            <Link to="/contact" className="vyg-link-gold mt-2" data-testid="footer-contact-cta">Plan your stay →</Link>
          </div>
        </div>
      </div>

      <div className="border-t border-parchment/20">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-parchment/85 sm:flex-row sm:px-6 lg:px-10">
          <p>© {new Date().getFullYear()} Vyagram Experiences. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-gold">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
